import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { Center, ProfessionalPicture, Row, Text } from '~/commonComponents';
import { getJobStatus } from '~/components/Dashboard/ReceiptList/utils';
import {
  IGetOnsiteInterviewListViewEntity,
  OnsiteInterviewStatus,
  OnsiteInterviewStatusMapper,
} from '~/domain/PermanentJobs';
import UserIcon from '~/images/icons/User';
import { JobStatusEnum } from '~/models';
import { Colors } from '~/themes/colors';

interface IListItemProps {
  data: IGetOnsiteInterviewListViewEntity;
}

export const ListItem = ({
  data: {
    id,
    localDate,
    localTimeEndsAt,
    localTimeStartsAt,
    professional,
    rate,
    status,
    totalCandidatesCount,
  },
}: IListItemProps) => {
  const navigate = useNavigate();

  const fmtStatus = getJobStatus(status);

  const handleRowClick = useCallback(() => {
    switch (status) {
      case JobStatusEnum.Counter:
      case JobStatusEnum.Open:
      case JobStatusEnum.Pending:
        navigate(`/dashboard/job/${id}`);
        break;
      default:
        navigate(`/dashboard/job/${id}/details`);
    }
  }, [id, navigate, status]);

  const Candidate = useMemo(() => {
    const onsiteInterviewStatus = OnsiteInterviewStatusMapper.fromJobStatus(status);

    if (onsiteInterviewStatus === OnsiteInterviewStatus.ActionNeeded) {
      return (
        <Row alignItems="center" justifyContent="center">
          <UserIcon color={Colors.secondary_500} />
          <Text bold color={Colors.secondary_500} fontSize={22} marginTop={6}>
            {totalCandidatesCount > 5 ? '5+' : totalCandidatesCount}
          </Text>
        </Row>
      );
    }

    if (
      [OnsiteInterviewStatus.Scheduled, OnsiteInterviewStatus.Completed].includes(
        onsiteInterviewStatus,
      )
    ) {
      return (
        <Center>
          <ProfessionalPicture
            imageUrl={professional?.imageUrl || ''}
            name={professional?.fullName || ''}
          />
        </Center>
      );
    }

    return <Row />;
  }, [professional?.fullName, professional?.imageUrl, status, totalCandidatesCount]);

  return (
    <Row
      alignItems="center"
      backgroundColor={Colors.white}
      borderRadius={10}
      boxShadow="0 1px 2px 0 rgba(0, 0, 0, 0.15), 0 1px 3px 1px rgba(0, 0, 0, 0.15)"
      cursor="pointer"
      display="grid"
      gridTemplateColumns="25% 25% 8% 20% 22%"
      height={80}
      onClick={handleRowClick}
    >
      <Text bold fontSize={18} paddingLeft={36}>
        {localDate}
      </Text>
      <Text>
        {localTimeStartsAt} - {localTimeEndsAt}
      </Text>
      <Text>{`$${rate.toFixed(2)}`}</Text>
      {Candidate}
      <Text color={fmtStatus.color} fontSize={18} paddingRight={36}>
        {fmtStatus.label}
      </Text>
    </Row>
  );
};
